import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ClipboardList, Plus, Loader2, Pencil, Trash2, Clock, Users, Calendar, Wrench, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PhotoGallery } from '@/components/PhotoGallery';

export function SiteJournal({ clientId }) {
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  const [entries, setEntries] = useState([]);
  const [fieldActivities, setFieldActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);

  // Form
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [hours, setHours] = useState('');
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);

  const loadEntries = async () => {
    try {
      const { data } = await api.get(`/worklogs/${clientId}`);
      setEntries(data.entries);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const loadFieldActivities = async () => {
    try {
      const { data } = await api.get(`/mobile/admin/field-activities/${clientId}`);
      setFieldActivities(data.activities || []);
    } catch { /* empty */ }
  };

  const loadEmployees = async () => {
    try {
      const { data } = await api.get('/employees', { params: { search: '', limit: 100 } });
      setEmployees((data.employees || []).filter(e => e.status === 'actif'));
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const loadSubcontractors = async () => {
    try {
      const { data } = await api.get('/subcontractors', { params: { search: '', limit: 100 } });
      setSubcontractors((data.subcontractors || []).filter(s => s.status === 'actif'));
    } catch (err) {
      console.error('Failed to load subcontractors:', err);
    }
  };

  useEffect(() => { loadEntries(); loadFieldActivities(); loadEmployees(); loadSubcontractors(); }, [clientId]);

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setTitle(''); setDesc(''); setHours(''); setSelectedEmps([]); setSelectedSubs([]);
    setEditingId(null);
  };

  const openNew = () => { resetForm(); loadEmployees(); loadSubcontractors(); setShowModal(true); };

  const openEdit = (entry) => {
    setEditingId(entry.entry_id);
    setDate(entry.date || '');
    setTitle(entry.title || '');
    setDesc(entry.description || '');
    setHours(entry.hours_worked?.toString() || '');
    setSelectedEmps(entry.employee_ids || []);
    setSelectedSubs(entry.subcontractor_ids || []);
    loadEmployees();
    loadSubcontractors();
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(tf('Titre requis', 'Title required'));
      return;
    }
    setSaving(true);
    const payload = {
      date, title: title.trim(), description: desc.trim(),
      hours_worked: parseFloat(hours) || 0,
      employee_ids: selectedEmps,
      subcontractor_ids: selectedSubs,
    };
    try {
      if (editingId) {
        await api.put(`/worklogs/entry/${editingId}`, payload);
        toast.success(tf('Entree mise a jour', 'Entry updated'));
      } else {
        await api.post(`/worklogs/${clientId}`, payload);
        toast.success(tf('Entree ajoutee', 'Entry added'));
      }
      setShowModal(false);
      resetForm();
      loadEntries();
    } catch (e) { toast.error(e.response?.data?.detail || tf('Erreur', 'Error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(tf('Supprimer cette entree?', 'Delete this entry?'))) return;
    try {
      await api.delete(`/worklogs/entry/${entryId}`);
      setEntries(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success(tf('Entree supprimee', 'Entry deleted'));
    } catch { toast.error(tf('Erreur', 'Error')); }
  };

  const toggleEmp = (empId) => {
    setSelectedEmps(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const toggleSub = (subId) => {
    setSelectedSubs(prev =>
      prev.includes(subId) ? prev.filter(id => id !== subId) : [...prev, subId]
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ClipboardList className="h-5 w-5" />
              {tf('Journal de chantier', 'Site journal')} ({entries.length})
            </CardTitle>
            <Button size="sm" onClick={openNew} data-testid="add-worklog-btn">
              <Plus className="h-4 w-4 mr-2" />
              {tf('Nouvelle entree', 'New entry')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{tf('Aucune entree', 'No entries')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map(entry => (
                <div key={entry.entry_id} className="border rounded-lg p-4 space-y-3" data-testid={`worklog-${entry.entry_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs font-mono">
                          <Calendar className="h-3 w-3 mr-1" />
                          {entry.date}
                        </Badge>
                        {entry.hours_worked > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {entry.hours_worked}h
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-semibold text-sm">{entry.title}</h4>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(entry)} data-testid={`edit-worklog-${entry.entry_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDelete(entry.entry_id)} data-testid={`delete-worklog-${entry.entry_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {entry.description && (
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{entry.description}</p>
                  )}

                  {entry.employees?.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      {entry.employees.map(emp => (
                        <Badge key={emp.id} variant="outline" className="text-xs">{emp.name}</Badge>
                      ))}
                    </div>
                  )}

                  {entry.subcontractors?.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                      {entry.subcontractors.map(sub => (
                        <Badge key={sub.id} variant="outline" className="text-xs bg-orange-50 border-orange-200">{sub.name}</Badge>
                      ))}
                    </div>
                  )}

                  <PhotoGallery entityType="worklog" entityId={entry.entry_id} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Activities / Pointages */}
      {fieldActivities.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5" />
              {tf('Pointages terrain', 'Field check-ins')} ({fieldActivities.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fieldActivities.map(fa => (
                <div key={fa.activity_id} className="border rounded-lg p-3 space-y-2" data-testid={`field-activity-${fa.activity_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs font-mono">
                        <Calendar className="h-3 w-3 mr-1" />{fa.date}
                      </Badge>
                      <span className="text-sm font-medium">{fa.employee_name}</span>
                    </div>
                    <Badge className={fa.clock_out ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}>
                      {fa.clock_out ? tf('Termine', 'Completed') : tf('En cours', 'Active')}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {tf('Arrivee', 'In')}: {new Date(fa.clock_in).toLocaleTimeString(language === 'fr' ? 'fr-CA' : 'en-CA', { hour: '2-digit', minute: '2-digit' })}
                      {fa.clock_in_location && (
                        <a
                          href={`https://www.google.com/maps?q=${fa.clock_in_location.lat},${fa.clock_in_location.lng}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline ml-1"
                          data-testid={`map-link-in-${fa.activity_id}`}
                        >
                          <MapPin className="h-3 w-3" />{tf('Carte', 'Map')}
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </span>
                    {fa.clock_out && (
                      <span className="flex items-center gap-1">
                        {tf('Depart', 'Out')}: {new Date(fa.clock_out).toLocaleTimeString(language === 'fr' ? 'fr-CA' : 'en-CA', { hour: '2-digit', minute: '2-digit' })}
                        {fa.clock_out_location && (
                          <a
                            href={`https://www.google.com/maps?q=${fa.clock_out_location.lat},${fa.clock_out_location.lng}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline ml-1"
                            data-testid={`map-link-out-${fa.activity_id}`}
                          >
                            <MapPin className="h-3 w-3" />{tf('Carte', 'Map')}
                            <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                      </span>
                    )}
                  </div>
                  {fa.notes && <p className="text-sm text-muted-foreground">{fa.notes}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              {editingId ? tf('Modifier l\'entree', 'Edit entry') : tf('Nouvelle entree', 'New entry')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="wl-date" />
              </div>
              <div className="space-y-1.5">
                <Label>{tf('Heures travaillees', 'Hours worked')}</Label>
                <Input type="number" step="0.5" min="0" value={hours} onChange={e => setHours(e.target.value)} placeholder="8" data-testid="wl-hours" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Titre', 'Title')}</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={tf('Ex: Jour 1 - Excavation', 'Ex: Day 1 - Excavation')} data-testid="wl-title" />
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Description des travaux', 'Work description')}</Label>
              <Textarea value={desc} onChange={e => setDesc(e.target.value)} rows={4} placeholder={tf('Detaillez les travaux effectues...', 'Detail the work done...')} data-testid="wl-description" />
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Employes presents', 'Employees present')} ({selectedEmps.length})</Label>
              {employees.length === 0 ? (
                <p className="text-xs text-muted-foreground">{tf('Aucun employe actif', 'No active employees')}</p>
              ) : (
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1" data-testid="wl-employees-list">
                  {employees.map(emp => (
                    <label
                      key={emp.employee_id}
                      className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-muted/50 transition-colors ${selectedEmps.includes(emp.employee_id) ? 'bg-orange-50 border border-orange-200' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmps.includes(emp.employee_id)}
                        onChange={() => toggleEmp(emp.employee_id)}
                        className="rounded accent-orange-500"
                      />
                      <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                      {emp.position && <Badge variant="outline" className="text-[10px] ml-auto">{emp.position}</Badge>}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{tf('Sous-traitants presents', 'Subcontractors present')} ({selectedSubs.length})</Label>
              {subcontractors.length === 0 ? (
                <p className="text-xs text-muted-foreground">{tf('Aucun sous-traitant actif', 'No active subcontractors')}</p>
              ) : (
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1" data-testid="wl-subcontractors-list">
                  {subcontractors.map(sub => (
                    <label
                      key={sub.subcontractor_id}
                      className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-muted/50 transition-colors ${selectedSubs.includes(sub.subcontractor_id) ? 'bg-orange-50 border border-orange-200' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSubs.includes(sub.subcontractor_id)}
                        onChange={() => toggleSub(sub.subcontractor_id)}
                        className="rounded accent-orange-500"
                      />
                      <span className="text-sm">{sub.first_name} {sub.last_name}</span>
                      {sub.specialty && <Badge variant="outline" className="text-[10px] ml-auto">{sub.specialty}</Badge>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>{tf('Annuler', 'Cancel')}</Button>
            <Button onClick={handleSave} disabled={saving || !title.trim()} data-testid="save-worklog-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingId ? tf('Mettre a jour', 'Update') : tf('Ajouter', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
